import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, CheckCircle2, AlertOctagon } from "lucide-react";
import { computePopoverPosition } from "../utils/popover-position.js";

// Popover dimensions for this indicator's portal popover. Positioning math is
// the shared `computePopoverPosition` (audit B-9).
const POPOVER_WIDTH = 256;
const POPOVER_HEIGHT_ESTIMATE = 180;
const POPOVER_GAP = 8;
const POPOVER_EDGE = 8;

const VARIANTS = {
  "auto-cast": {
    label: "Auto",
    Icon: Sparkles,
    chipClass:
      "border-brand-primary/30 bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/15 dark:border-brand-primary/40 dark:bg-brand-primary/15 dark:text-blue-300",
  },
  "auto-submitted": {
    label: "Auto",
    Icon: CheckCircle2,
    chipClass:
      "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  },
};

const OVERRIDE_CHIP_CLASS =
  "border-amber-400 bg-amber-100 text-amber-800 ring-1 ring-amber-300 hover:bg-amber-200 dark:border-amber-500 dark:bg-amber-950/50 dark:text-amber-200";

function voteLabel(value) {
  if (value === "PREFERRED") return "Preferred";
  if (value === "FEASIBLE") return "Feasible";
  return null;
}

/**
 * Tier-aware "we voted for you" chip. Phase 3 M5.
 *
 * Visible per slot to communicate that the engine cast / submitted a vote on
 * the user's behalf. The hover popover shows the previous manual vote (if
 * any) plus a Restore button that hands control back to the user.
 *
 * Modes:
 *   "auto-cast"      Tier 1 — engine pre-filled (voteProposed=true) but
 *                    nothing has been written to votes[slot] yet. Restore =
 *                    "don't auto-vote this slot." Caller is expected to
 *                    add the slot to userTouchedSlots so the engine doesn't
 *                    re-fire on the next reeval.
 *   "auto-submitted" Tier 2/3 — engine wrote votes[slot]. Restore reverts to
 *                    previousManualVote ?? null. Caller clears voteAutoSource
 *                    + voteProposed and adds the slot to userTouchedSlots.
 *
 * isOverride toggles the amber-strong "we changed your vote" treatment used
 * by Tier 3 when the engine overrode a manual vote.
 */
export function SlotAutoIndicator({
  mode = null,
  previousManualVote = null,
  isOverride = false,
  reasonText = null,
  onRestore,
  disabled = false,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);
  // Set when opened/entered via keyboard so a layout effect moves focus into
  // the portaled dialog (the portal lives at document.body, unreachable by Tab).
  const focusInRef = useRef(false);

  function focusFirstInPopover() {
    const el = popoverRef.current?.querySelector(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (el) el.focus();
  }

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(ev) {
      const inWrapper =
        wrapperRef.current && wrapperRef.current.contains(ev.target);
      const inPopover =
        popoverRef.current && popoverRef.current.contains(ev.target);
      if (!inWrapper && !inPopover) {
        setOpen(false);
      }
    }
    function onKey(ev) {
      if (ev.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    function update() {
      setPosition(
        computePopoverPosition(triggerRef.current, {
          width: POPOVER_WIDTH,
          heightEstimate: POPOVER_HEIGHT_ESTIMATE,
          gap: POPOVER_GAP,
          edge: POPOVER_EDGE,
        })
      );
    }
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (open && focusInRef.current) {
      focusInRef.current = false;
      focusFirstInPopover();
    }
    if (!open) focusInRef.current = false;
  }, [open]);

  function cancelHoverClose() {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  }
  function handleMouseEnter() {
    cancelHoverClose();
    setOpen(true);
  }
  function handleMouseLeave() {
    cancelHoverClose();
    hoverCloseTimerRef.current = setTimeout(() => setOpen(false), 150);
  }
  useEffect(() => () => cancelHoverClose(), []);

  if (!mode || !VARIANTS[mode]) return null;

  const variant = VARIANTS[mode];
  const chipClass = isOverride ? OVERRIDE_CHIP_CLASS : variant.chipClass;
  const Icon = isOverride ? AlertOctagon : variant.Icon;
  const chipLabel = isOverride ? "Override" : variant.label;

  const previousLabel = voteLabel(previousManualVote);

  // Restore button label varies by mode + previousManualVote presence.
  let buttonLabel;
  let buttonValue;
  if (mode === "auto-cast") {
    buttonLabel = "Don't auto-vote this slot";
    buttonValue = null;
  } else if (previousLabel) {
    buttonLabel = `Restore my ${previousLabel} vote`;
    buttonValue = previousManualVote;
  } else {
    buttonLabel = "Clear this vote";
    buttonValue = null;
  }

  function handleRestore(ev) {
    ev.stopPropagation();
    if (disabled) return;
    if (typeof onRestore === "function") {
      onRestore(buttonValue);
    }
    setOpen(false);
  }

  const popoverContent = open ? (
    <div
      ref={popoverRef}
      // role="dialog" (not tooltip): contains the interactive Restore control.
      role="dialog"
      aria-label={`${chipLabel} vote details`}
      aria-modal="false"
      style={{
        position: "fixed",
        left: `${position.left}px`,
        top: `${position.top}px`,
        width: `${POPOVER_WIDTH}px`,
        zIndex: 50,
      }}
      onMouseEnter={cancelHoverClose}
      onMouseLeave={handleMouseLeave}
      onBlur={(e) => {
        if (
          popoverRef.current?.contains(e.relatedTarget) ||
          e.relatedTarget === triggerRef.current
        ) {
          return;
        }
        handleMouseLeave();
      }}
      className="rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-800"
    >
      {isOverride && (
        <p className="mb-2 rounded-md border-l-2 border-amber-500 bg-amber-50 px-2 py-1.5 text-[12px] font-semibold text-amber-800 dark:border-amber-400 dark:bg-amber-900/30 dark:text-amber-200">
          ⚠ We changed your vote.
        </p>
      )}
      <p className="mb-1 font-semibold text-slate-700 dark:text-slate-200">
        {isOverride
          ? "We changed an earlier vote on this slot"
          : mode === "auto-cast"
            ? "We pre-filled this vote for you"
            : "We voted on this slot for you"}
      </p>
      {isOverride && previousLabel && (
        <p className="mb-2 text-slate-600 dark:text-slate-300">
          The earlier vote was <strong>{previousLabel}</strong> — either set by
          you or auto-cast from one of your free-time blocks. Your latest
          schedule means this slot is a different fit now.
        </p>
      )}
      {reasonText && (
        <p className="mb-2 text-slate-600 dark:text-slate-300">{reasonText}</p>
      )}
      {!isOverride && previousLabel && mode === "auto-submitted" && (
        <p className="mb-2 text-slate-600 dark:text-slate-300">
          Your previous vote: <strong>{previousLabel}</strong>
        </p>
      )}
      <button
        type="button"
        disabled={disabled}
        onMouseDown={(ev) => ev.stopPropagation()}
        onClick={handleRestore}
        className="mt-1 w-full rounded-md bg-brand-primary px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {buttonLabel}
      </button>
    </div>
  ) : null;

  return (
    <>
      <span
        ref={wrapperRef}
        className="relative inline-flex"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          ref={triggerRef}
          type="button"
          data-testid={testId || "slot-auto-indicator"}
          aria-label={chipLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            // ArrowDown moves focus INTO the body-portaled dialog (Tab can't
            // reach it). Enter/Space keep their native toggle via onClick.
            if (e.key === "ArrowDown") {
              e.preventDefault();
              e.stopPropagation();
              if (open) {
                focusFirstInPopover();
              } else {
                focusInRef.current = true;
                setOpen(true);
              }
            }
          }}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${chipClass}`}
        >
          <Icon className="h-3 w-3" aria-hidden="true" />
          <span>{chipLabel}</span>
        </button>
      </span>
      {popoverContent && createPortal(popoverContent, document.body)}
    </>
  );
}
